import Link from 'next/link';
import React, { Fragment, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import { bannerTypes } from '@/constants/types';
import { contentsAPI } from '@/utils/contentsApi';
import { EXTERNAL } from '@/constants';
import { cn } from '@/utils/cn';
import BannerImageComponent from '@/app/components/Image/BannerImageComponent';
import Button from '@/app/components/Button';
import DynamicSectionTitle from '@/app/components/Typography/DynamicSectionTitle';
import ButtonLabel from '@/app/components/Typography/ButtonLabel';

export default function CampaignPopularDeals({ section }) {
  const router = useRouter();
  const pathName = usePathname();
  const [data, setData] = useState([]);
  const { lang } = useSelector((state) => state.globalSettings);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contentsData = {
          sectionId: section?.id,
          contentType: section?.content_type,
        };
        const response = await contentsAPI(contentsData);
        const parsedItems =
          typeof response === 'string' ? JSON.parse(response) : response;
        const sortedItems = [...parsedItems].sort(
          (a, b) => a.priority - b.priority,
        );
        setData(sortedItems);
      } catch (err) {
        console.warn('Error fetching data:', err);
      }
    };

    fetchData();
  }, [section]);

  const handleClickUrl = (e, url) => {
    e.stopPropagation();
    e.preventDefault();

    if (!url) return null;

    if (url?.en?.type === 'web') {
      router.push(url?.en?.url);
    } else if (url?.en?.type === 'external') {
      window.open(url?.en?.url, '_blank');
    }
  };

  const renderImageLayout = () => {
    if (data?.length === 3) {
      return (
        <>
          <div className="relative mb-8 lg:mb-20 overflow-hidden rounded-[12px] lg:rounded-[24px]">
            <BannerImageComponent
              desktopSrc={data[0]?.image?.large}
              mobileSrc={data[0]?.image?.small}
              alt={data[0]?.name?.en}
              className="rounded-xl lg:rounded-3xl"
              type={bannerTypes.popularDeals1}
            />

            {data[0]?.redirect_url?.en?.url ? (
              <Link
                prefetch={false}
                aria-label="Go to campaign popular deals item"
                href={data[0]?.redirect_url?.en?.url}
                target={
                  data[0]?.redirect_url?.en?.type === EXTERNAL
                    ? '_blank'
                    : '_self'
                }
                passHref
              >
                <Button
                  type="outline"
                  className="w-fit h-[22px] lg:h-[40px] mx-auto !py-4 lg:!py-10 !px-16 !bg-white border !border-himalayan-poppy absolute bottom-[5px] lg:bottom-[24px] left-0 right-0"
                  onClick={(e) => handleClickUrl(e, data[0]?.redirect_url)}
                >
                  <ButtonLabel
                    label={
                      data[0]?.redirect_url?.[lang]?.text ??
                      data[0]?.redirect_url?.en?.text
                    }
                    className="text-xs lg:text-base text-tealgreen font-semibold"
                  />
                </Button>
              </Link>
            ) : null}
          </div>
          <div className="flex gap-4">
            {data?.slice(1).map((subItem, subIndex) => (
              <div
                key={subItem?.id || subIndex}
                className="relative w-full rounded-[12px] lg:rounded-[24px] overflow-hidden"
              >
                <BannerImageComponent
                  desktopSrc={subItem?.image?.large}
                  mobileSrc={subItem?.image?.small}
                  alt={subItem?.title?.en}
                  type={bannerTypes.popularDeals23}
                />

                {subItem?.redirect_url?.en?.url ? (
                  <Link
                    prefetch={false}
                    aria-label="Go to campaign popular deals item"
                    href={subItem?.redirect_url?.en?.url}
                    target={
                      subItem?.redirect_url?.en?.type === EXTERNAL
                        ? '_blank'
                        : '_self'
                    }
                    passHref
                  >
                    <Button
                      type="outline"
                      className="w-fit h-[22px] lg:h-[40px] mx-auto !py-4 lg:!py-10 !px-16 !bg-white border !border-himalayan-poppy absolute bottom-[5px] lg:bottom-[24px] left-0 right-0"
                      onClick={(e) => handleClickUrl(e, subItem?.redirect_url)}
                    >
                      <ButtonLabel
                        label={
                          subItem?.redirect_url?.[lang]?.text ??
                          subItem?.redirect_url?.en?.text
                        }
                        className="text-xs lg:text-base text-tealgreen font-semibold"
                      />
                    </Button>
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </>
      );
    }

    if (data.length === 2) {
      return (
        <div className="flex gap-4">
          {data.map((subItem, subIndex) => (
            <div
              key={subItem?.id || subIndex}
              className="relative w-full rounded-[12px] lg:rounded-[24px] overflow-hidden"
            >
              <BannerImageComponent
                desktopSrc={subItem?.image?.large}
                mobileSrc={subItem?.image?.small}
                alt={subItem?.title?.en}
                type={bannerTypes.popularDeals23}
              />
              {subItem?.redirect_url?.en?.url ? (
                <Link
                  prefetch={false}
                  aria-label="Go to campaign popular deals item"
                  href={subItem?.redirect_url?.en?.url}
                  target={
                    subItem?.redirect_url?.en?.type === EXTERNAL
                      ? '_blank'
                      : '_self'
                  }
                  passHref
                >
                  <Button
                    type="outline"
                    className="w-fit h-[22px] lg:h-[40px] mx-auto !py-4 lg:!py-10 !px-16 !bg-white border !border-himalayan-poppy absolute bottom-[5px] lg:bottom-[24px] left-0 right-0"
                    onClick={(e) => handleClickUrl(e, subItem?.redirect_url)}
                  >
                    <ButtonLabel
                      label={
                        subItem?.redirect_url?.[lang]?.text ??
                        subItem?.redirect_url?.en?.text
                      }
                      className="text-xs lg:text-base text-tealgreen font-semibold"
                    />
                  </Button>
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="relative mb-8 lg:mb-20 rounded-[12px] lg:rounded-[24px] overflow-hidden">
        {Array.isArray(data) && data?.length > 0
          ? data?.map((item) => (
              <Fragment key={item?.title?.en}>
                <BannerImageComponent
                  desktopSrc={item?.image?.large}
                  mobileSrc={item?.image?.small}
                  alt={item?.title?.en}
                  type={bannerTypes.popularDeals1}
                />

                {item?.redirect_url?.en?.url ? (
                  <Link
                    prefetch={false}
                    aria-label="Go to campaign popular deals item"
                    href={item?.redirect_url?.en?.url}
                    target={
                      item?.redirect_url?.en?.type === EXTERNAL
                        ? '_blank'
                        : '_self'
                    }
                    passHref
                  >
                    <Button
                      type="outline"
                      className="w-fit h-[22px] lg:h-[40px] mx-auto !py-4 lg:!py-10 !px-16 !bg-white border !border-himalayan-poppy absolute bottom-[5px] lg:bottom-[24px] left-0 right-0"
                      onClick={(e) => handleClickUrl(e, item?.redirect_url)}
                    >
                      <ButtonLabel
                        label={
                          item?.redirect_url?.[lang]?.text ??
                          item?.redirect_url?.en?.text
                        }
                        className="text-xs lg:text-base text-tealgreen font-semibold"
                      />
                    </Button>
                  </Link>
                ) : null}
              </Fragment>
            ))
          : null}
      </div>
    );
  };

  return (
    <div className="campaign-popular-deals container mx-auto relative overflow-hidden mt-24 mb-[24px] lg:mb-[48px]">
      <DynamicSectionTitle
        text={section?.title[lang]}
        className={cn('mb-8 lg:mb-20', {
          'text-center normal-case': pathName === '/',
        })}
      />
      {renderImageLayout()}
    </div>
  );
}
